// Lazy-loaded image resizer using Pica
let pica = null;

async function loadPica() {
  if (!pica) {
    const Pica = (await import('pica')).default;
    pica = Pica();
  }
  return pica;
}

/**
 * Resize an image file to a target size (square, cropped from center)
 * @param {File|Blob} file - The image file to resize
 * @param {number} targetSize - Target size in pixels (will be square)
 * @returns {Promise<Blob>} - Resized image as JPEG blob
 */
export async function resizeImage(file, targetSize) {
  const picaInstance = await loadPica();
  
  // Create image bitmap from file
  const img = await createImageBitmap(file);
  
  // Create source canvas
  const sourceCanvas = document.createElement('canvas');
  sourceCanvas.width = img.width;
  sourceCanvas.height = img.height;
  const ctx = sourceCanvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  
  // Calculate dimensions for center crop to square
  const minDim = Math.min(img.width, img.height);
  const sx = (img.width - minDim) / 2;
  const sy = (img.height - minDim) / 2;
  
  // Create cropped canvas (square)
  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = minDim;
  croppedCanvas.height = minDim;
  croppedCanvas.getContext('2d').drawImage(
    sourceCanvas, 
    sx, sy, minDim, minDim,  // source region
    0, 0, minDim, minDim     // destination region
  );
  
  // Resize with Pica to target size
  const targetCanvas = document.createElement('canvas');
  targetCanvas.width = targetSize;
  targetCanvas.height = targetSize;
  
  await picaInstance.resize(croppedCanvas, targetCanvas, {
    quality: 3, // Highest quality (Lanczos filter)
    alpha: true,
    unsharpAmount: 80,
    unsharpRadius: 0.6,
    unsharpThreshold: 2
  });
  
  // Convert to Blob (JPEG with 90% quality)
  return new Promise((resolve) => {
    targetCanvas.toBlob(resolve, 'image/jpeg', 0.90);
  });
}

/**
 * Resize an image to multiple sizes
 * @param {File|Blob} file - The image file to resize
 * @param {number[]} sizes - Array of target sizes
 * @returns {Promise<Object>} - Object mapping size to blob
 */
export async function resizeImageToMultipleSizes(file, sizes) {
  const results = {};
  
  for (const size of sizes) {
    results[size] = await resizeImage(file, size);
  }
  
  return results;
}

